import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { HeroBoard } from '../../../data/hero-motif/hero-motif.data';

export type RGB = [ number, number, number ];

export interface SolidBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// a touch screen has no pointer to hover: a motif idles a little awake and surges now and then, as
// the circuit board does (see HeroCircuitComponent)
const TOUCH_IDLE = 0.3;
const TOUCH_SURGE = 0.75;
const SURGE_EVERY = 5;
const SURGE_SPREAD = 4;
const TOUCH_LINGER = 1.6;

/**
 * What every theme's hero motif shares (see HERO_MOTIFS): a canvas filling the host, sized to it and
 * redrawn at once on a resize; a loop outside Angular that runs only while the board is on screen and
 * the tab is visible; how awake the board is (`energy`, eased toward the pointer over the stage, a
 * finger, the downloads counter's charge or a short pulse); a touch screen's idle surges; the theme's
 * colours (its colour circle, and the page's --theme-color* vars) and whether the surface is light;
 * the boxes of the hero's logo, motto and counter; and the motion's seamless loop: `time` runs from
 * 0 to `loop` and starts again, so a motif whose waves turn whole numbers of times in `loop` never
 * jumps. Reduced motion, and the page's wallpaper (`frozen`), draw one still moment.
 *
 * A motif lays itself out in `build()` (with a seeded random, so a resize re-lays the same board) and
 * paints a frame in `paint()`, in page pixels.
 */
@Directive()
export abstract class HeroMotifBase implements AfterViewInit, OnDestroy, HeroBoard {

  @ViewChild( 'canvas', { static: true } ) canvasRef!: ElementRef<HTMLCanvasElement>;

  /** A still board, drawn once (the page's wallpaper). */
  @Input() frozen = false;
  /** How awake the still moment is (0..1). */
  @Input() storm = 0.6;
  /** The hero's logo, motto and counter. */
  @Input() solids: HTMLElement[] = [];
  /** The element the pointer wakes the board through (the hero stage); listened to outside Angular. */
  @Input() stage: HTMLElement | null = null;

  /** The theme's five colours (its colour circle, `compactPalette`; owner, 2026-10-16: all five). */
  @Input() set palette( value: string[] | null | undefined ) {
    this.paletteColors = ( value || [] ).map( hex => this.hex( hex ) ).filter( ( c ): c is RGB => !!c );
    this.colorsChanged();
    if ( this.still && this.ctx ) { this.draw(); }
  }

  // ---- what a motif tunes ----

  /** The canvas' pixels per page pixel (soft motifs draw at half and let the browser scale up). */
  protected res = 1;
  /** Seconds of motif time in one seamless loop. */
  protected loop = 40;
  /** How fast `energy` follows what wakes it (per second). */
  protected ease = 1.8;
  /** How awake the counter's charge makes the board. */
  protected chargeLevel = 1;
  /** How awake a still board under reduced motion is. */
  protected stillEnergy = 0.35;
  /** A cap on the frames drawn per second (0 = every animation frame); time still runs on every frame. */
  protected maxFps = 0;

  protected abstract build( rand: () => number ): void;

  protected abstract paint( ctx: CanvasRenderingContext2D, dt: number ): void;

  /** How fast motif time runs now (1 = real time); by default quicker when awake. */
  protected speed(): number {
    return 0.32 + 0.9 * this.energy;
  }

  /** The colours changed (theme, palette or light / dark): a motif drops what it painted from them. */
  protected colorsChanged(): void {
  }

  // ---- what a motif reads ----

  protected ctx: CanvasRenderingContext2D | null = null;
  protected width = 0;
  protected height = 0;
  protected time = 0;
  protected energy = 0;
  /** The pointer (or finger) over the board, in page pixels; -1e4 when there is none. */
  protected pointerX = -1e4;
  protected pointerY = -1e4;
  /** The pointer's presence, eased (0..1), for a glow that follows it softly. */
  protected near = 0;
  protected paletteColors: RGB[] = [];
  /** The page's --theme-color, --theme-color-secondary and --theme-color-tertiary. */
  protected colors: RGB[] = [ [ 103, 153, 220 ], [ 77, 193, 177 ], [ 187, 113, 155 ] ];
  protected lightSurface = false;
  protected blocks: SolidBox[] = [];
  protected still = false;

  private raf = 0;
  private last = 0;
  private sinceDraw = 0;
  private readonly seed = Math.floor( Math.random() * 0x7fffffff );
  private blocksAge = 0;
  private target = 0;
  private hover = 0;
  private charged = 0;
  private pulsed = 0;
  private pulseAt = -1;
  private pulseFor = 3;
  private touch = false;
  private idle = 0;
  private surgeAt = -1;
  private surgeIn = 3 + Math.random() * 2;
  private touching = false;
  private held = 0;
  private lastTouch = -1e9;
  private clientX = NaN;
  private clientY = NaN;
  private colorAge = 99;
  private visible = true;
  private resizeObserver: ResizeObserver | null = null;
  private intersection: IntersectionObserver | null = null;
  private themeWatch: MutationObserver | null = null;
  private stageListeners: [ string, EventListener ][] = [];
  private probe: HTMLSpanElement | null = null;
  private onVisibility = () => this.sync();

  protected constructor( private zone: NgZone, private host: ElementRef<HTMLElement> ) {
  }

  ngAfterViewInit(): void {
    this.time = Math.random() * this.loop;
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext( '2d' );
    if ( !this.ctx ) { return; }
    this.still = this.frozen || ( !!window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
    this.touch = !this.still && !!window.matchMedia && window.matchMedia( '(hover: none)' ).matches;
    this.probe = document.createElement( 'span' );
    this.probe.style.display = 'none';
    this.host.nativeElement.appendChild( this.probe );

    this.zone.runOutsideAngular( () => {
      this.resizeObserver = new ResizeObserver( () => this.resize() );
      this.resizeObserver.observe( this.host.nativeElement );
      this.intersection = new IntersectionObserver( entries => {
        this.visible = entries.some( e => e.isIntersecting );
        this.sync();
      }, { threshold: 0.02 } );
      this.intersection.observe( this.host.nativeElement );
      document.addEventListener( 'visibilitychange', this.onVisibility );
      const root = document.getElementById( 'app-component' ) || document.body;
      this.themeWatch = new MutationObserver( () => this.recolor() );
      this.themeWatch.observe( root, { attributes: true, attributeFilter: [ 'class', 'style' ] } );
      if ( this.stage ) {
        // the mouse events a tap makes up are skipped (see HeroCircuitComponent)
        this.stageListeners = [
          [ 'mouseenter', () => { if ( !this.fromTouch() ) { this.excite( true ); } } ],
          [ 'mouseleave', () => { if ( !this.fromTouch() ) { this.excite( false ); } } ],
          [ 'mousemove', ( ev: Event ) => {
            if ( !this.fromTouch() ) { this.pointer( ( ev as MouseEvent ).clientX, ( ev as MouseEvent ).clientY ); }
          } ],
          [ 'touchstart', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchmove', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchend', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchcancel', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
        ];
        for ( const [ type, fn ] of this.stageListeners ) { this.stage.addEventListener( type, fn, { passive: true } ); }
      }
      this.readColors();
      this.resize();
      this.sync();
    } );
  }

  ngOnDestroy(): void {
    this.stop();
    for ( const [ type, fn ] of this.stageListeners ) { this.stage?.removeEventListener( type, fn ); }
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    document.removeEventListener( 'visibilitychange', this.onVisibility );
    this.themeWatch?.disconnect();
    this.probe?.remove();
  }

  // ---- what wakes the board ----

  excite( on: boolean ): void {
    this.hover = on ? 1 : 0;
    if ( !on ) {
      this.clientX = NaN;
      this.clientY = NaN;
      this.pointerX = -1e4;
      this.pointerY = -1e4;
    }
    this.retarget();
  }

  /** The downloads counter is loading and counting: the board wakes as under the pointer (owner, 2026-10-16). */
  charge( on: boolean ): void {
    this.charged = on ? this.chargeLevel : 0;
    this.retarget();
  }

  pulse( seconds = 3 ): void {
    this.pulseFor = seconds;
    this.pulseAt = 0;
    this.sync();
  }

  pointer( clientX: number, clientY: number ): void {
    this.clientX = clientX;
    this.clientY = clientY;
  }

  private retarget(): void {
    this.target = Math.max( this.hover, this.charged, this.idle, this.pulsed );
    this.sync();
  }

  private touched( ev: TouchEvent ): void {
    this.lastTouch = performance.now();
    const finger = ev.touches[ 0 ];
    if ( finger ) {
      this.touching = true;
      this.held = 0;
      this.pointer( finger.clientX, finger.clientY );
      if ( !this.hover ) { this.excite( true ); }
    } else if ( this.touching ) {
      this.touching = false;
      this.held = TOUCH_LINGER;
    }
  }

  private fromTouch(): boolean {
    return performance.now() - this.lastTouch < 1000;
  }

  /** A touch screen's own wakefulness, a lifted finger's wake running out, and the pulse's sin² swell. */
  private breathe( dt: number ): void {
    if ( !this.touching && this.held > 0 ) {
      this.held -= dt;
      if ( this.held <= 0 ) {
        this.held = 0;
        this.excite( false );
      }
    }
    if ( this.touch ) {
      this.surgeIn -= dt;
      if ( this.surgeIn <= 0 && this.surgeAt < 0 ) {
        this.surgeAt = 0;
        this.surgeIn = SURGE_EVERY + Math.random() * SURGE_SPREAD;
      }
      let lift = 0;
      if ( this.surgeAt >= 0 ) {
        this.surgeAt += dt;
        const t = this.surgeAt;
        lift = t < 0.8 ? t / 0.8 : t < 2.2 ? 1 : Math.max( 0, 1 - ( t - 2.2 ) / 1.6 );
        if ( t >= 3.8 ) { this.surgeAt = -1; }
      }
      this.idle = TOUCH_IDLE + ( TOUCH_SURGE - TOUCH_IDLE ) * lift;
    }
    if ( this.pulseAt >= 0 ) {
      this.pulseAt += dt;
      const p = this.pulseAt / this.pulseFor;
      this.pulsed = p >= 1 ? 0 : Math.pow( Math.sin( Math.PI * p ), 2 );
      if ( p >= 1 ) { this.pulseAt = -1; }
    }
    this.target = Math.max( this.hover, this.charged, this.idle, this.pulsed );
  }

  // ---- the board ----

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = this.host.nativeElement.getBoundingClientRect();
    if ( rect.width < 10 || rect.height < 10 ) { return; }
    const scale = this.scale();
    this.width = Math.round( rect.width );
    this.height = Math.round( rect.height );
    canvas.width = Math.max( 1, Math.round( this.width * scale ) );
    canvas.height = Math.max( 1, Math.round( this.height * scale ) );
    canvas.style.width = this.width + 'px';
    canvas.style.height = this.height + 'px';
    this.measureSolids();
    this.build( this.seeded( this.seed ) );
    if ( this.still ) { this.energy = this.frozen ? this.storm : this.stillEnergy; }
    // a new size clears the canvas: draw again at once, so it never flashes empty until the next frame
    this.draw();
  }

  /** Canvas pixels per page pixel: the motif's own `res`, times the screen's density (capped at 2) when it is 1. */
  private scale(): number {
    return this.res < 1 ? this.res : Math.min( 2, window.devicePixelRatio || 1 ) * this.res;
  }

  private measureSolids(): void {
    this.blocksAge = 0;
    const host = this.host.nativeElement.getBoundingClientRect();
    this.blocks = ( this.solids || [] ).map( el => {
      const r = el.getBoundingClientRect();
      return { x: r.left - host.left, y: r.top - host.top, w: r.width, h: r.height };
    } ).filter( b => b.w > 4 && b.h > 4 );
  }

  /** A small deterministic generator: one seed, one board, however often it is re-laid at a size. */
  private seeded( seed: number ): () => number {
    let s = seed >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  // ---- the loop ----

  private sync(): void {
    const run = this.visible && document.visibilityState === 'visible' && !this.still;
    if ( run && !this.raf ) {
      this.last = performance.now();
      this.zone.runOutsideAngular( () => {
        this.raf = requestAnimationFrame( t => this.frame( t ) );
      } );
    } else if ( !run ) {
      this.stop();
    }
  }

  private stop(): void {
    if ( this.raf ) {
      cancelAnimationFrame( this.raf );
      this.raf = 0;
    }
  }

  private frame( now: number ): void {
    this.raf = 0;
    const dt = Math.max( 0, Math.min( 0.05, ( now - this.last ) / 1000 ) );
    this.last = now;
    this.blocksAge += dt;
    if ( this.solids?.length && this.blocksAge > 1 ) { this.measureSolids(); }
    if ( !isNaN( this.clientX ) ) {
      const rect = this.host.nativeElement.getBoundingClientRect();
      this.pointerX = this.clientX - rect.left;
      this.pointerY = this.clientY - rect.top;
    }
    this.breathe( dt );
    this.energy += ( this.target - this.energy ) * Math.min( 1, dt * this.ease );
    if ( this.energy < 0.001 ) { this.energy = 0; }
    this.near += ( ( this.pointerX > -1e3 ? 1 : 0 ) - this.near ) * Math.min( 1, dt * 3 );
    this.time = ( this.time + dt * this.speed() ) % this.loop;
    this.colorAge += dt;
    if ( this.colorAge > 1.5 ) { this.readColors(); }
    // a motif that caps its frame rate draws every few frames, with the time that has passed
    this.sinceDraw += dt;
    if ( !this.maxFps || this.sinceDraw >= 1 / this.maxFps - 0.004 ) {
      this.draw( this.sinceDraw );
      this.sinceDraw = 0;
    }
    this.raf = requestAnimationFrame( t => this.frame( t ) );
  }

  // ---- the drawing ----

  private recolor(): void {
    this.readColors();
    if ( this.still ) { this.draw(); }
  }

  private draw( dt = 0 ): void {
    const ctx = this.ctx;
    if ( !ctx || !this.width ) { return; }
    const scale = this.canvasRef.nativeElement.width / this.width;
    ctx.setTransform( scale, 0, 0, scale, 0, 0 );
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect( 0, 0, this.width, this.height );
    this.paint( ctx, dt );
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /** The theme's three colours and whether the surface under the board is light, as the page resolves them now. */
  private readColors(): void {
    this.colorAge = 0;
    if ( !this.probe ) { return; }
    const style = getComputedStyle( this.host.nativeElement );
    const read = ( name: string, fallback: RGB ): RGB => {
      const value = style.getPropertyValue( name ).trim();
      if ( !value ) { return fallback; }
      this.probe!.style.color = value;
      const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec( getComputedStyle( this.probe! ).color );
      return m ? [ +m[ 1 ], +m[ 2 ], +m[ 3 ] ] : fallback;
    };
    const next: RGB[] = [
      read( '--theme-color', this.colors[ 0 ] ),
      read( '--theme-color-secondary', this.colors[ 1 ] ),
      read( '--theme-color-tertiary', this.colors[ 2 ] ),
    ];
    const surface = read( '--background-color', [ 20, 43, 55 ] );
    const light = ( 0.2126 * surface[ 0 ] + 0.7152 * surface[ 1 ] + 0.0722 * surface[ 2 ] ) > 140;
    const changed = light !== this.lightSurface || next.some( ( c, i ) => c.some( ( v, k ) => Math.abs( v - this.colors[ i ][ k ] ) > 1 ) );
    this.colors = next;
    this.lightSurface = light;
    if ( changed ) { this.colorsChanged(); }
  }

  // ---- colour helpers ----

  protected hex( value: string ): RGB | null {
    const m = /^#?([0-9a-f]{6})$/i.exec( ( value || '' ).trim() );
    if ( !m ) { return null; }
    const n = parseInt( m[ 1 ], 16 );
    return [ ( n >> 16 ) & 255, ( n >> 8 ) & 255, n & 255 ];
  }

  protected mix( c: RGB, d: RGB, k: number ): RGB {
    return [ c[ 0 ] + ( d[ 0 ] - c[ 0 ] ) * k, c[ 1 ] + ( d[ 1 ] - c[ 1 ] ) * k, c[ 2 ] + ( d[ 2 ] - c[ 2 ] ) * k ];
  }

  protected rgba( c: RGB, alpha: number ): string {
    return `rgba(${ Math.round( c[ 0 ] ) },${ Math.round( c[ 1 ] ) },${ Math.round( c[ 2 ] ) },${ alpha })`;
  }
}
